/**
 * Reine Animations-Engine der konsolidierten WhatsApp-Phone-Komponente,
 * getrennt von der React-/Markup-Schicht, damit das Verhalten (Loop, runOnce,
 * Reveal, reduced-motion, IntersectionObserver-Start) ohne vollen DOM
 * deterministisch mit Fake-Timern getestet werden kann.
 *
 * Die Logik ist 1:1 aus den drei früheren Effekten (whatsapp-demo.tsx,
 * service/whatsapp-phone.tsx, ui/whatsapp-chat.tsx) zusammengeführt – KEINE
 * Timing-Werte geändert.
 */

export type WaStep = { id: string; show: boolean; hold: number };

/* ------------------------- explainer (Onboarding) ------------------------- */

/** Phasen des Onboarding-Connect-Explainers. */
export type WaExplainerPhase = 'compose' | 'sent' | 'typing' | 'reply';

/**
 * Timings (ms, relativ zum Loop-Start) des Connect-Explainers. Werden als Props
 * an `WhatsappPhone` gereicht – NIE in der Komponente hartkodiert (dumb-Regel).
 * Defaults exakt aus dem Design-Handoff (`Onboarding.dc.html:355-359`).
 */
export interface WaExplainerTimings {
  /** Wechsel `compose → sent` (Token wird abgeschickt). */
  sent: number;
  /** Wechsel `sent → typing` (Locari tippt). */
  typing: number;
  /** Wechsel `typing → reply` (Willkommensnachricht erscheint). */
  reply: number;
  /** Loop-Reset zurück auf `compose`. */
  loop: number;
}

/** Design-Default-Timings (2,4 / 3,8 / 5,3 / 12,5 s). */
export const WA_EXPLAINER_TIMINGS: WaExplainerTimings = {
  sent: 2400,
  typing: 3800,
  reply: 5300,
  loop: 12500,
};

/** Minimal-Schnittstelle, die beide Engines vom `messages`-Root brauchen. */
interface EngineEl {
  classList: {
    toggle(token: string, force?: boolean): void;
    add(token: string): void;
    remove(token: string): void;
    contains(token: string): boolean;
  };
}
interface EngineRoot {
  querySelector(selector: string): EngineEl | null;
  querySelectorAll(selector: string): Iterable<EngineEl>;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Startet die Engine entweder sofort (`autoStart`) oder beim Eintritt in den
 * Viewport (IntersectionObserver, threshold .25 / rootMargin -10%). Liefert die
 * Cleanup-Funktion.
 */
function startWhenReady(
  root: EngineRoot & Partial<Element>,
  autoStart: boolean,
  start: () => void,
  hasStarted: () => boolean,
): () => void {
  let observer: IntersectionObserver | null = null;

  if (autoStart) {
    start();
  } else if (
    typeof window !== 'undefined' &&
    'IntersectionObserver' in window
  ) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasStarted()) {
            start();
            observer?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25, rootMargin: '0px 0px -10% 0px' },
    );
    observer.observe(root as unknown as Element);
  } else {
    start();
  }

  return () => observer?.disconnect();
}

/* ------------------------------- steps engine ----------------------------- */

export interface StepsEngineOptions {
  steps: WaStep[];
  autoStart: boolean;
  runOnce: boolean;
  onComplete?: () => void;
}

/** Loop+reset (Default) oder runOnce-Hold→onComplete. */
export function runStepsEngine(
  root: EngineRoot & Partial<Element>,
  { steps, autoStart, runOnce, onComplete }: StepsEngineOptions,
): () => void {
  // querySelectorAll (nicht querySelector): eine Step-ID darf MEHRERE Elemente
  // enthüllen – so erscheint ein Day-Separator zusammen mit seiner Folge-Message
  // (gleiche data-wa-step), nie allein.
  const setVisible = (id: string, on: boolean) => {
    for (const el of root.querySelectorAll(`[data-wa-step="${id}"]`)) {
      el.classList.toggle('is-visible', on);
    }
  };
  const resetAll = () => {
    for (const el of root.querySelectorAll('[data-wa-step]')) {
      el.classList.remove('is-visible');
    }
  };
  const showAll = () => {
    for (const el of root.querySelectorAll('[data-wa-step]')) {
      if (el.classList.contains('whatsapp-typing')) continue;
      el.classList.add('is-visible');
    }
  };

  if (prefersReducedMotion()) {
    showAll();
    if (runOnce) onComplete?.();
    return () => {};
  }

  let timer: ReturnType<typeof setTimeout> | null = null;
  let i = 0;
  let stopped = false;

  const tick = () => {
    if (stopped) return;
    const s = steps[i];
    if (!s) return;
    if (s.id === 'reset') resetAll();
    else setVisible(s.id, s.show);

    const isLast = i === steps.length - 1;
    if (runOnce && isLast) {
      timer = setTimeout(() => {
        if (!stopped) onComplete?.();
      }, s.hold);
      return;
    }

    i = (i + 1) % steps.length;
    timer = setTimeout(tick, s.hold);
  };
  const start = () => {
    if (timer) return;
    i = 0;
    resetAll();
    timer = setTimeout(tick, 500);
  };

  const stopObserver = startWhenReady(root, autoStart, start, () => !!timer);

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    stopObserver();
  };
}

/* ------------------------------ reveal engine ----------------------------- */

type RevealAnimStep =
  | { type: 'show'; index: number; hold: number }
  | { type: 'typing-on'; index: number; hold: number }
  | { type: 'typing-off'; index: number; hold: number };

export interface RevealEngineOptions {
  count: number;
}

/** Einmaliger Reveal (ab Bubble #2 mit Tipp-Indikator), bleibt am Ende stehen. */
export function runRevealEngine(
  root: EngineRoot & Partial<Element>,
  { count }: RevealEngineOptions,
): () => void {
  const stepEl = (i: number) => root.querySelector(`[data-wac-step="${i}"]`);
  const typingEl = (i: number) =>
    root.querySelector(`[data-wac-typing="${i}"]`);

  const resetAll = () => {
    for (const el of root.querySelectorAll(
      '[data-wac-step],[data-wac-typing]',
    )) {
      el.classList.remove('is-visible');
    }
  };
  const showAll = () => {
    for (let i = 0; i < count; i += 1) stepEl(i)?.classList.add('is-visible');
  };

  if (prefersReducedMotion()) {
    showAll();
    return () => {};
  }

  const seq: RevealAnimStep[] = [];
  for (let i = 0; i < count; i += 1) {
    if (i > 0) {
      seq.push({ type: 'typing-on', index: i, hold: 1000 });
      seq.push({ type: 'typing-off', index: i, hold: 140 });
    }
    seq.push({ type: 'show', index: i, hold: 1900 });
  }

  let timer: ReturnType<typeof setTimeout> | null = null;
  let i = 0;
  let stopped = false;

  const tick = () => {
    if (stopped) return;
    const s = seq[i];
    if (!s) return;
    if (s.type === 'typing-on') {
      typingEl(s.index)?.classList.add('is-visible');
    } else if (s.type === 'typing-off') {
      typingEl(s.index)?.classList.remove('is-visible');
    } else {
      typingEl(s.index)?.classList.remove('is-visible');
      stepEl(s.index)?.classList.add('is-visible');
    }
    i += 1;
    if (i < seq.length) timer = setTimeout(tick, s.hold);
  };
  const start = () => {
    if (timer) return;
    i = 0;
    resetAll();
    timer = setTimeout(tick, 500);
  };

  const stopObserver = startWhenReady(root, false, start, () => !!timer);

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    stopObserver();
  };
}
